import { getXsrfToken } from '../api/getXsrfToken';
/**
 * Payment request status values returned by the invoicing API.
 */
export const PaymentRequestStatus = {
    SENT: 'sent',
};
/** Prefix applied to a quote ID to form its invoicing `documentRef`. */
export const QUOTE_DOCUMENT_REF_PREFIX = 'hr-quote-';
/** Converts a quote ID into the `documentRef` used by the invoicing API. */
export const toQuoteDocumentRef = (quoteId) => `${QUOTE_DOCUMENT_REF_PREFIX}${quoteId}`;
/**
 * Sums the processingBalance of every payment request, representing the total
 * amount currently in-transit (e.g. an ACH payment that has not yet settled).
 */
export const calculateInTransitAmount = (paymentRequests) => paymentRequests.reduce((sum, paymentRequest) => { var _a; return sum + ((_a = paymentRequest.processingBalance) !== null && _a !== void 0 ? _a : 0); }, 0);
const EMPTY_RESPONSE = {
    meta: { totalCount: 0, totalAmount: 0 },
    data: [],
};
const fetchOptions = () => {
    var _a;
    return ({
        credentials: 'include',
        redirect: 'error',
        headers: { 'x-xsrf-token': (_a = getXsrfToken()) !== null && _a !== void 0 ? _a : '' },
    });
};
const buildQuery = ({ documentRef, status, customerRef, includeExpired, }) => {
    const params = new URLSearchParams({ documentRef });
    if (status) {
        params.append('status', status);
    }
    if (customerRef) {
        params.append('customerRef', customerRef);
    }
    if (includeExpired) {
        params.append('includeExpired', 'true');
    }
    return params.toString();
};
/**
 * Requests that are currently in flight, keyed by their query string.
 */
const inFlightRequests = new Map();
const getPaymentRequests = async (query) => {
    const response = await fetch(`/api/2.0/invoicing/payment-requests?${query}`, fetchOptions());
    if (response.status === 401) {
        return EMPTY_RESPONSE;
    }
    if (!response.ok) {
        throw new Error(`Failed to fetch payment requests: ${response.status}`);
    }
    return response.json();
};
/**
 * Fetches payment requests for a document from the invoicing API.
 */
export const fetchPaymentRequests = (params) => {
    const query = buildQuery(params);
    const existingRequest = inFlightRequests.get(query);
    if (existingRequest) {
        return existingRequest;
    }
    const request = getPaymentRequests(query).finally(() => {
        inFlightRequests.delete(query);
    });
    inFlightRequests.set(query, request);
    return request;
};
/**
 * Cancels every outstanding payment request for a document.
 */
export const cancelPaymentRequests = async ({ documentRef, customerRef, }) => {
    const params = new URLSearchParams({ documentRef });
    if (customerRef) {
        params.append('customerRef', customerRef);
    }
    const response = await fetch(`/api/2.0/invoicing/payment-requests/cancel?${params.toString()}`, Object.assign(Object.assign({}, fetchOptions()), { method: 'POST' }));
    if (response.status === 401) {
        return;
    }
    if (!response.ok) {
        throw new Error(`Failed to cancel payment requests: ${response.status}`);
    }
};
